// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: the client for every calendar route of calendar-plan.md section 11 (the phone-sync route is the phones'
// and is left out). A refusal keeps the server's message as it is; one without a message reads "The request failed
// (HTTP <status>).", and no answer at all "The request failed." (section 12). Exclude from upstream sync.

import {fill, CalendarStrings as S} from '@app/features/calendar/CalendarStrings';
import type {
	Calendar,
	CalendarColorsAnswer,
	CalendarEvent,
	CalendarEventCreateBody,
	CalendarEventPatchBody,
	CalendarEventsAnswer,
	CalendarUpdateBody,
} from '@app/features/calendar/CalendarTypes';
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';

export const CalendarEndpoints = {
	ME: '/calendars/@me',
	CALENDAR: (calendarId: string) => `/calendars/${calendarId}`,
	COLORS: (calendarId: string) => `/calendars/${calendarId}/colors`,
	MY_COLOR: (calendarId: string) => `/calendars/${calendarId}/colors/@me`,
	EVENTS: (calendarId: string) => `/calendars/${calendarId}/events`,
	EVENT: (calendarId: string, eventId: string) => `/calendars/${calendarId}/events/${eventId}`,
} as const;

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null;
}

export class CalendarApiError extends Error {
	readonly status: number | null;
	readonly code: string | null;
	/** What the user is shown: the server's message, else section 12's fallback. */
	readonly displayMessage: string;

	constructor(status: number | null, code: string | null, serverMessage: string | null) {
		const displayMessage = serverMessage ?? (status == null ? S.requestFailed : fill(S.requestFailedStatus, {status}));
		super(displayMessage);
		this.name = 'CalendarApiError';
		this.status = status;
		this.code = code;
		this.displayMessage = displayMessage;
	}
}

/** A stale save: 409 CALENDAR_EVENT_CONFLICT, with the server's current event. */
export class CalendarConflictError extends CalendarApiError {
	readonly event: CalendarEvent;

	constructor(message: string | null, event: CalendarEvent) {
		super(409, 'CALENDAR_EVENT_CONFLICT', message);
		this.name = 'CalendarConflictError';
		this.event = event;
	}
}

export function toCalendarError(error: unknown): CalendarApiError {
	if (error instanceof CalendarApiError) return error;
	if (error instanceof HttpError) {
		const body = isRecord(error.body) ? error.body : null;
		const code = typeof body?.code === 'string' ? body.code : null;
		const message = typeof body?.message === 'string' && body.message.length > 0 ? body.message : null;
		if (error.status === 409 && code === 'CALENDAR_EVENT_CONFLICT' && isRecord(body?.event)) {
			return new CalendarConflictError(message, body.event as unknown as CalendarEvent);
		}
		return new CalendarApiError(error.status, code, message);
	}
	return new CalendarApiError(null, null, null);
}

async function call<T>(run: () => Promise<{body: T}>): Promise<T> {
	try {
		return (await run()).body;
	} catch (error) {
		throw toCalendarError(error);
	}
}

export interface CalendarApi {
	/** null when the user is in no calendar (404). */
	getMyCalendar(): Promise<Calendar | null>;
	updateCalendar(calendarId: string, body: CalendarUpdateBody): Promise<Calendar>;
	getColors(calendarId: string): Promise<CalendarColorsAnswer>;
	setMyColor(calendarId: string, color: string): Promise<CalendarColorsAnswer>;
	/** Every live event. */
	listEvents(calendarId: string): Promise<CalendarEventsAnswer>;
	listEventsBetween(calendarId: string, from: number, to: number): Promise<CalendarEventsAnswer>;
	/** Every event changed after the revision, tombstones included. */
	listEventsSince(calendarId: string, revision: number): Promise<CalendarEventsAnswer>;
	createEvent(calendarId: string, body: CalendarEventCreateBody): Promise<CalendarEvent>;
	/** A stale if_revision throws CalendarConflictError. */
	updateEvent(calendarId: string, eventId: string, body: CalendarEventPatchBody): Promise<CalendarEvent>;
	/** The tombstone. */
	deleteEvent(calendarId: string, eventId: string): Promise<CalendarEvent>;
}

export const RestCalendarApi: CalendarApi = {
	async getMyCalendar() {
		try {
			return (await http.get<Calendar>(CalendarEndpoints.ME)).body;
		} catch (error) {
			if (error instanceof HttpError && error.status === 404) return null;
			throw toCalendarError(error);
		}
	},
	updateCalendar: (calendarId, body) =>
		call(() => http.patch<Calendar>(CalendarEndpoints.CALENDAR(calendarId), {body})),
	getColors: (calendarId) => call(() => http.get<CalendarColorsAnswer>(CalendarEndpoints.COLORS(calendarId))),
	setMyColor: (calendarId, color) =>
		call(() => http.put<CalendarColorsAnswer>(CalendarEndpoints.MY_COLOR(calendarId), {body: {color}})),
	listEvents: (calendarId) => call(() => http.get<CalendarEventsAnswer>(CalendarEndpoints.EVENTS(calendarId))),
	listEventsBetween: (calendarId, from, to) =>
		call(() => http.get<CalendarEventsAnswer>(CalendarEndpoints.EVENTS(calendarId), {query: {from, to}})),
	listEventsSince: (calendarId, revision) =>
		call(() =>
			http.get<CalendarEventsAnswer>(CalendarEndpoints.EVENTS(calendarId), {query: {since_revision: revision}}),
		),
	createEvent: (calendarId, body) => call(() => http.post<CalendarEvent>(CalendarEndpoints.EVENTS(calendarId), {body})),
	updateEvent: (calendarId, eventId, body) =>
		call(() => http.patch<CalendarEvent>(CalendarEndpoints.EVENT(calendarId, eventId), {body})),
	deleteEvent: (calendarId, eventId) =>
		call(() => http.delete<CalendarEvent>(CalendarEndpoints.EVENT(calendarId, eventId))),
};
